/**
 * Signin.js - Login success: store API_SIGN_IN response and navigate to HistopathologyHome.
 * No second API; all storage from sign-in response only.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Checkbox, FormControlLabel, IconButton, Paper, TextField, Typography } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import Logo from "../Assets/Logo/Logo.svg";
import "./Signin.css";
import "./Forgotpassword.css";
import RestAPIService from "../Utility/Services/RestAPIService";
import { setCachevalue, clearAllCachevalue } from "../Utility/Services/CacheProviderService";
import { Serviceurls } from "../Utility/API/Serviceurls";
import Dialog from "../Components/Dialog";
import Loader from "../Components/Loader";
import Failed from "../Assets/Animation/Failed.gif";
import Success from "../Assets/Animation/Success.gif";
import ToastMsg from "../Components/ToastMsg/ToastMsg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { ConfirmPopup } from "../Components/Common Components/CommonComponents";
import { usePermissions } from "../Utility/PermissionContext";
import { getFirstAllowedRoute } from "../Utility/Services/RoleAccessService";

const t = (key) => ({ userName: "User Name", Password: "Password", Message: "Message", Close: "Close" }[key] || key);

export default function Signin() {
  const navigate = useNavigate();
  const { updatePermissions } = usePermissions();
  const REMEMBER_KEY = "emedhub_remembered_user";
  const [username, setUsername] = useState(() => {
    try { return localStorage.getItem(REMEMBER_KEY) || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    try { return !!localStorage.getItem(REMEMBER_KEY); } catch { return false; }
  });
  const [usernameError, setUsernameError] = useState("");
  const [usernameHelperText, setUsernameHelperText] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordHelperText, setPasswordHelperText] = useState("");
  const [isLoader, setIsLoader] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [openFailed, setOpenFailed] = useState(false);
  const [isResetPass, setIsResetPass] = useState(false);
  const [isResetDays, setIsResetDays] = useState("0");
  const [skipforNow, setSkipforNow] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [isSuccessMsg, setIsSuccessMsg] = useState(false);
  const [isSuccessMsgText, setIsSuccessMsgText] = useState("");

  useEffect(() => {
    clearAllCachevalue();
  }, []);

  const validate = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "username") {
      setUsername(value);
      setUsernameError("");
      setUsernameHelperText("");
    }
    if (name === "password") {
      setPassword(value);
      setPasswordError("");
      setPasswordHelperText("");
    }
  }, []);

  const msgClose = useCallback(() => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setIsSuccessMsg(false);
    setIsSuccessMsgText("");
  }, []);

  const dialogCloseFunction = useCallback(() => {
    window.location.reload();
    setLoginSuccess(false);
  }, []);

  const failesDialogClose = useCallback(() => {
    setOpenFailed(false);
  }, []);

  const resetPassword = useCallback(() => {
    navigate("/ResetPassword", { state: { userName: username } });
  }, [navigate, username]);

  const submit = useCallback(
    (e) => {
      if (e && e.preventDefault) e.preventDefault();
      try {
        if (username && password) {
          clearAllCachevalue();
          setIsLoader(true);
          const data = { user_name: username, password };
          RestAPIService.create(data, Serviceurls.API_SIGN_IN)
            .then((response) => {
              // Support both wrapped ({ status, data }) and flat ({ token, user_id, ... }) response
              const reply = response?.data ?? response;
              const d = reply?.data ?? reply;
              const isSuccess = d && (d.token != null || d.user_id != null);

              if (isSuccess) {
                if (d.is_first_login) {
                  setIsLoader(false);
                  navigate("/ResetPassword", { state: { userName: username } });
                  return;
                }
                if (!d.is_alert || skipforNow) {
                  try {
                    if (rememberMe) localStorage.setItem(REMEMBER_KEY, username);
                    else localStorage.removeItem(REMEMBER_KEY);
                  } catch {}
                  setCachevalue(d.token, "LoginAuthToken");
                  setCachevalue("true", "isLoggedIn");
                  setCachevalue(d.token, "AccessToken");
                  setCachevalue(d.user_name ?? "", "LogedUserName");
                  setCachevalue(String(d.user_id ?? ""), "loggedUserId");
                  setCachevalue(String(d.branch_id ?? ""), "BranchId");
                  setCachevalue(d.branch_name ?? d.barnch_name ?? "", "BranchName");
                  setCachevalue(
                    JSON.stringify({
                      token: d.token,
                      user_id: d.user_id,
                      user_name: d.user_name,
                      organization_id: d.organization_id,
                      branch_id: d.branch_id,
                      histopathology_id: d.histopathology_id,
                      module_name: "Histopathology",
                    }),
                    "loggedInUserInfo"
                  );
                  setCachevalue(String(d.auto_logout_minutes ?? 15), "auto_log_out_time");
                  setCachevalue(String(d.transaction_number_mandatory === true), "is_transaction_number_mandatory");
                  setCachevalue(String(d.bill_amount_auto_fetched === true), "auto_fetch_bill_amount_to_payment");
                  setCachevalue(String(d.enable_cash_limit_per_patient === true), "enable_cash_limit_per_patient");
                  setCachevalue(String(d.need_credit_type_option_popup_in_billing === true), "need_credit_type_option_popup_in_billing");
                  setCachevalue(JSON.stringify(d.organization_id ?? null), "org_id");
                  const screenAccess = Array.isArray(d.permission_access) ? d.permission_access : [];
                  setCachevalue(JSON.stringify(screenAccess), "Scrren_access");
                  updatePermissions({
                    ...d,
                    package_access: d?.package_access || { modules: [] },
                  });
                  setIsLoader(false);
                  const firstRoute = getFirstAllowedRoute(screenAccess);
                  navigate(firstRoute, { replace: true, state: { NewLogin: true } });
                  return;
                } else {
                  setIsLoader(false);
                  setIsResetPass(!!d?.is_alert);
                  setIsResetDays(String(d?.balance_days ?? "0"));
                }
              } else {
                setIsLoader(false);
                setOpenFailed(true);
              }
            })
            .catch((err) => {
              if (err?.response?.data?.message === "Invalid username") {
                setIsLoader(false);
                setOpenFailed(false);
                setUsernameError("error");
                setUsernameHelperText(err.response.data.message);
              } else if (err?.response?.data?.message === "Invalid password") {
                setIsLoader(false);
                setOpenFailed(false);
                setPasswordError("error");
                setPasswordHelperText(err.response.data.message);
              } else {
                setIsLoader(false);
                setUsernameError("error");
                setUsernameHelperText(err?.response?.data?.message);
                setIsErrorMsg(true);
                setIsErrorMsgText(err?.response?.data?.message || err?.message || "Error");
                setIsAdmin(!!(err?.response?.data?.is_account_locked && err?.response?.data?.is_admin_user));
                setTimeout(() => setIsErrorMsg(false), 2000);
              }
            });
        } else {
          setCachevalue("false", "isLoggedIn");
          if (!username) {
            setUsernameError("error");
            setUsernameHelperText("Enter Valid username");
          } else {
            setUsernameError("");
            setUsernameHelperText("");
          }
          if (!password) {
            setPasswordError("error");
            setPasswordHelperText("Enter valid password");
          } else {
            setPasswordError("");
            setPasswordHelperText("");
          }
        }
      } catch (err) {
        if (err?.response?.data?.message === "Invalid username") {
          setIsLoader(false);
          setOpenFailed(false);
          setUsernameError("error");
          setUsernameHelperText(err.response.data.message);
        } else if (err?.response?.data?.message === "Invalid password") {
          setIsLoader(false);
          setOpenFailed(false);
          setPasswordError("error");
          setPasswordHelperText(err.response.data.message);
        } else {
          setIsErrorMsg(true);
          setIsErrorMsgText(err?.message || "Error");
          setTimeout(() => setIsErrorMsg(false), 2000);
        }
      }
    },
    [username, password, skipforNow, navigate, updatePermissions, rememberMe]
  );

  const popClose = useCallback(() => {
    setIsResetPass(false);
    setSkipforNow(true);
    setTimeout(() => submit(), 0);
  }, [submit]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #e0f7fa 0%, #b2dfdb 35%, #80cbc4 70%, #4db6ac 100%)",
        position: "relative",
        overflow: "hidden",
        p: 2,
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-120px",
          left: "-120px",
          width: "320px",
          height: "320px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 70%)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-140px",
          right: "-100px",
          width: "360px",
          height: "360px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(13,148,136,0.35) 0%, rgba(13,148,136,0) 70%)",
          pointerEvents: "none",
        },
      }}
    >
      <Loader loaderOpen={isLoader} />

      <Paper
        elevation={6}
        sx={{
          width: "100%",
          maxWidth: { xs: 420, md: 900 },
          minHeight: { md: 480 },
          borderRadius: "14px",
          overflow: "hidden",
          backgroundColor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(6px)",
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Left – animation panel */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            p: 4,
            background:
              "linear-gradient(160deg, #134e4a 0%, #0d9488 55%, #14b8a6 100%)",
            color: "#fff",
            position: "relative",
            overflow: "hidden",
            "@keyframes bubbleRise": {
              "0%": { transform: "translateY(0)", opacity: 0 },
              "30%": { opacity: 1 },
              "100%": { transform: "translateY(-50px)", opacity: 0 },
            },
            "@keyframes liquidWave": {
              "0%, 100%": { transform: "translateX(0)" },
              "50%": { transform: "translateX(-3px)" },
            },
            "@keyframes lineAppear": {
              "0%, 100%": { transform: "scaleX(0)", opacity: 0 },
              "20%, 80%": { transform: "scaleX(1)", opacity: 1 },
            },
            "@keyframes checkPop": {
              "0%, 60%": { transform: "scale(0)", opacity: 0 },
              "75%": { transform: "scale(1.2)", opacity: 1 },
              "100%": { transform: "scale(1)", opacity: 1 },
            },
            "@keyframes float": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-8px)" },
            },
            "@keyframes spin": {
              "0%": { transform: "rotate(0deg)" },
              "100%": { transform: "rotate(360deg)" },
            },
            "&::before": {
              content: '""',
              position: "absolute",
              top: "-80px",
              right: "-80px",
              width: "240px",
              height: "240px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
              pointerEvents: "none",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-100px",
              left: "-60px",
              width: "260px",
              height: "260px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
              pointerEvents: "none",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 3, position: "relative", zIndex: 1 }}>
            {/* Test tube */}
            <Box sx={{ animation: "float 3s ease-in-out infinite" }}>
              <svg width="90" height="130" viewBox="0 0 42 60" fill="none">
                <path d="M14 4 V40 Q14 50 21 50 Q28 50 28 40 V4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" fill="none" />
                <path d="M10 4 H32" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />
                <path d="M14 26 Q17 24 21 26 Q25 28 28 26 V40 Q28 50 21 50 Q14 50 14 40 Z" fill="#5eead4"
                  style={{ animation: "liquidWave 2s ease-in-out infinite", transformOrigin: "center" }} />
                <circle cx="19" cy="38" r="1.8" fill="#fff" opacity="0.9"
                  style={{ animation: "bubbleRise 2.4s ease-in 0s infinite" }} />
                <circle cx="23" cy="42" r="1.4" fill="#fff" opacity="0.9"
                  style={{ animation: "bubbleRise 2.4s ease-in 0.8s infinite" }} />
                <circle cx="20" cy="44" r="1.2" fill="#fff" opacity="0.9"
                  style={{ animation: "bubbleRise 2.4s ease-in 1.4s infinite" }} />
              </svg>
            </Box>

            {/* Microscope-like atom (spinning ring) */}
            <Box sx={{ animation: "float 3s ease-in-out infinite 0.3s" }}>
              <svg width="100" height="130" viewBox="0 0 60 60" fill="none">
                <circle cx="30" cy="30" r="6" fill="#5eead4" />
                <g style={{ animation: "spin 6s linear infinite", transformOrigin: "30px 30px" }}>
                  <ellipse cx="30" cy="30" rx="22" ry="9" stroke="#fff" strokeWidth="1.6" fill="none" />
                  <ellipse cx="30" cy="30" rx="22" ry="9" stroke="#fff" strokeWidth="1.6" fill="none" transform="rotate(60 30 30)" />
                  <ellipse cx="30" cy="30" rx="22" ry="9" stroke="#fff" strokeWidth="1.6" fill="none" transform="rotate(120 30 30)" />
                </g>
              </svg>
            </Box>

            {/* Report */}
            <Box sx={{ animation: "float 3s ease-in-out infinite 0.6s" }}>
              <svg width="100" height="130" viewBox="0 0 48 60" fill="none">
                <rect x="6" y="6" width="36" height="48" rx="3" fill="#fff" stroke="#fff" strokeWidth="2" />
                <rect x="12" y="14" width="20" height="2.4" rx="1.2" fill="#0d9488"
                  style={{ transformOrigin: "left center", animation: "lineAppear 3s ease-in-out 0.2s infinite" }} />
                <rect x="12" y="22" width="24" height="2.4" rx="1.2" fill="#94a3b8"
                  style={{ transformOrigin: "left center", animation: "lineAppear 3s ease-in-out 0.5s infinite" }} />
                <rect x="12" y="30" width="18" height="2.4" rx="1.2" fill="#94a3b8"
                  style={{ transformOrigin: "left center", animation: "lineAppear 3s ease-in-out 0.8s infinite" }} />
                <rect x="12" y="38" width="22" height="2.4" rx="1.2" fill="#94a3b8"
                  style={{ transformOrigin: "left center", animation: "lineAppear 3s ease-in-out 1.1s infinite" }} />
                <circle cx="36" cy="48" r="6" fill="#0d9488"
                  style={{ transformOrigin: "center", animation: "checkPop 3s ease-in-out infinite" }} />
                <path d="M33 48 l2 2 l4 -4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"
                  style={{ transformOrigin: "center", animation: "checkPop 3s ease-in-out infinite" }} />
              </svg>
            </Box>
          </Box>

          <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, letterSpacing: 0.3 }}>
              Histopathology Lab & Reports
            </Typography>
            <Typography sx={{ mt: 1, fontSize: "0.9rem", opacity: 0.85, maxWidth: 320 }}>
              From specimen to signed-off report — all in one place.
            </Typography>
          </Box>
        </Box>

        {/* Right – login form */}
        <Box sx={{ flex: 1, p: { xs: 3, sm: 4, md: 5 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
          <img src={Logo} alt="logo" style={{ width: 56, height: 56 }} />
          <Typography sx={{ mt: 1.5, fontSize: "1.25rem", fontWeight: 700, color: "#1a1a1a" }}>
            Welcome To eMedHub
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: "0.85rem", color: "#666" }}>
            Sign in to continue
          </Typography>
        </Box>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(e);
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              error={!!usernameError}
              helperText={usernameHelperText}
              name="username"
              inputProps={{ emed_tid: "username" }}
              value={username}
              onChange={validate}
              label={t("userName")}
            />

            <TextField
              fullWidth
              size="small"
              error={!!passwordError}
              helperText={passwordHelperText}
              value={password}
              inputProps={{ emed_tid: "password" }}
              name="password"
              onChange={validate}
              label={t("Password")}
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((p) => !p)} edge="end">
                      {showPassword ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& input::-ms-reveal, & input::-ms-clear": { display: "none !important" },
                "& input::-webkit-credentials-auto-fill-button, & input::-webkit-strong-password-auto-fill-button, & input::-webkit-contacts-auto-fill-button": {
                  display: "none !important",
                  visibility: "hidden !important",
                  pointerEvents: "none !important",
                  position: "absolute !important",
                  right: "0 !important",
                },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: -0.5 }}>
              <FormControlLabel
                sx={{ "& .MuiTypography-root": { fontSize: "0.85rem", color: "#555" } }}
                control={
                  <Checkbox
                    size="small"
                    checked={rememberMe}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setRememberMe(v);
                      try { if (!v) localStorage.removeItem(REMEMBER_KEY); } catch {}
                    }}
                  />
                }
                label="Remember Me"
              />
            </Box>

            <Button
              fullWidth
              variant="contained"
              emed_tid="signin"
              type="submit"
              sx={{ textTransform: "none", fontWeight: 600, py: 1 }}
            >
              Sign in
            </Button>

            {isAdmin && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Typography
                  onClick={() => navigate("/UnlockAccount")}
                  sx={{ fontSize: "0.85rem", color: "#1976d2", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  Unlock Your Account ?
                </Typography>
              </Box>
            )}
          </Box>
        </form>

        {loginSuccess && (
          <Dialog
            type="loginsucess"
            DialogClose={dialogCloseFunction}
            header="Message"
            content="Login Successfully !"
            icon={<img className="eMed_dilboxAnimations" alt="Success" src={Success} />}
            secondary="Close"
          />
        )}

        {openFailed && (
          <Dialog
            type="Notification"
            DialogClose={failesDialogClose}
            header="Message"
            content={usernameHelperText}
            icon={<img className="eMed_dilboxAnimations" alt="Failed" src={Failed} />}
            secondary="Close"
            closeButton={dialogCloseFunction}
          />
        )}
        </Box>
      </Paper>

      {isErrorMsg && (
        <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />
      )}
      {isSuccessMsg && (
        <ToastMsg severity="success" msg={isSuccessMsgText} msgPop={msgClose} />
      )}

      {isResetPass && (
        <ConfirmPopup
          cnfTitle="Reset Password"
          cnfContent={
            isResetDays === "0" || isResetDays === 0
              ? "Your password will expire, so please reset your password."
              : `Your password will expire in ${isResetDays} days, so please reset your password.`
          }
          contentBold="bold"
          cnfAlertPopupClose={popClose}
          btnText1="Skip for Now"
          btnText2="Reset Password"
          btnType="error"
          btn1Hide={isResetDays === "0" || isResetDays === 0}
          cnfData={resetPassword}
        />
      )}
    </Box>
  );
}
